import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import type { SelectedRegion } from 'features/bug-report/types'

type Props = {
  onSelect: (region: SelectedRegion) => void
  onCancel: () => void
}

type Point = { x: number; y: number }

const MIN_REGION_PX = 10

export const RegionSelectionOverlay: React.FC<Props> = ({
  onSelect,
  onCancel,
}) => {
  const [start, setStart] = useState<Point | null>(null)
  const [end, setEnd] = useState<Point | null>(null)
  const onCancelRef = useRef(onCancel)
  onCancelRef.current = onCancel

  useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancelRef.current()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [])

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    setStart({ x: e.clientX, y: e.clientY })
    setEnd({ x: e.clientX, y: e.clientY })
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!start) return
    setEnd({ x: e.clientX, y: e.clientY })
  }

  const handleMouseUp = (): void => {
    if (!start || !end) {
      onCancel()
      return
    }
    const x = Math.min(start.x, end.x)
    const y = Math.min(start.y, end.y)
    const width = Math.abs(end.x - start.x)
    const height = Math.abs(end.y - start.y)
    if (width < MIN_REGION_PX || height < MIN_REGION_PX) {
      onCancel()
      return
    }
    onSelect({ x, y, width, height })
  }

  const selectionStyle =
    start && end
      ? {
          left: Math.min(start.x, end.x),
          top: Math.min(start.y, end.y),
          width: Math.abs(end.x - start.x),
          height: Math.abs(end.y - start.y),
        }
      : null

  return (
    <Overlay
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <Hint>Drag to select an area · Esc to cancel</Hint>
      {selectionStyle && <Selection style={selectionStyle} />}
    </Overlay>
  )
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 10000;
  cursor: crosshair;
  user-select: none;
`

const Hint = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
`

const Selection = styled.div`
  position: fixed;
  border: 2px dashed #fff;
  background: rgba(255, 255, 255, 0.1);
  pointer-events: none;
`
